import { cn } from "@/shadcn/lib/utils";
import {
  SelectContent,
  SelectItem,
  Select as SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/ui/select";

/**
 * Radix reserves the empty string for "no value", so a selectable "leave this unset" entry needs a
 * sentinel of its own. It never leaves this module: `onChange` maps it back to `null`.
 */
const CLEAR_VALUE = "__select-none__";

export type SelectOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
};

export type SelectProps = {
  /** `null` shows the placeholder — the control holds no value. */
  readonly "value": string | null;
  readonly "options": readonly SelectOption[];
  readonly "onChange": (value: string | null) => void;
  readonly "placeholder"?: string;
  /**
   * Adds a leading entry that clears the field back to no value, and keeps it selectable after a
   * value has been chosen. Omit it for a field the offer always has to carry a value for — there
   * the only way out is picking another option.
   */
  readonly "clearOptionLabel"?: string;
  readonly "disabled"?: boolean;
  readonly "aria-label"?: string;
  readonly "data-testid"?: string;
  /** Sizing/width for the trigger; the field styling below is shared. */
  readonly "triggerClassName"?: string;
};

/**
 * The canonical single-select for standalone (non-form-bound) use.
 *
 * Composed on `@/shadcn/ui/select`, whose content is portaled to the body — the reason this exists
 * rather than an inline menu: these selects sit inside scrolling, virtualized rows, and any
 * in-document panel gets clipped by the nearest `overflow` ancestor. The portal also lands above a
 * Dialog panel (`z-121`) and an elevated modal (`z-131`) on the primitive's own `z-200`.
 *
 * For a react-hook-form field use `form/fields/RadixSelectField` instead; this one is for controls
 * whose value is owned by a parent component.
 */
export const Select = ({
  value,
  options,
  onChange,
  placeholder,
  clearOptionLabel,
  disabled = false,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  triggerClassName,
}: SelectProps) => (
  <SelectRoot
    disabled={disabled}
    value={value ?? ""}
    onValueChange={(next) => onChange(next === CLEAR_VALUE ? null : next)}
  >
    <SelectTrigger
      aria-label={ariaLabel}
      className={cn(
        // Matches the TextField these sit beside: same height, ring, and focus treatment.
        "h-10 rounded border-none bg-white px-4 text-body1 text-primary shadow-none ring-1 ring-light-grey ring-offset-1 focus:ring-1 focus:ring-black",
        triggerClassName,
      )}
      data-testid={dataTestId}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {clearOptionLabel != null && <SelectItem value={CLEAR_VALUE}>{clearOptionLabel}</SelectItem>}
      {options.map((option) => (
        <SelectItem disabled={option.disabled} key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </SelectRoot>
);
